import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";

export interface DREAccountItem {
  accountId: string;
  accountCode: string;
  accountName: string;
  amount: number;
}

export interface DREData {
  period: { from: string; to: string };

  // RECEITA BRUTA
  grossRevenue: {
    productSales: number;
    serviceTax: number;
    deliveryFees: number;
    total: number;
  };

  // DEDUÇÕES
  deductions: {
    discounts: number;
    cancellations: number;
    total: number;
  };

  // RECEITA LÍQUIDA
  netRevenue: number;

  // CMV
  cmv: {
    total: number;
  };

  // LUCRO BRUTO
  grossProfit: number;
  grossMarginPercent: number;

  // DESPESAS OPERACIONAIS
  operatingExpenses: {
    byAccount: DREAccountItem[];
    total: number;
  };

  // RESULTADO OPERACIONAL (EBITDA)
  operatingResult: number;
  operatingMarginPercent: number;

  // DESPESAS FINANCEIRAS
  financialExpenses: {
    cardFees: number;
    otherFees: number;
    total: number;
  };

  // LUCRO LÍQUIDO
  netProfit: number;
  netMarginPercent: number;
}

interface UseDREReportParams {
  dateFrom: string;
  dateTo: string;
}

export function useDREReport({ dateFrom, dateTo }: UseDREReportParams) {
  const { activeRestaurant } = useRestaurantContext();

  return useQuery({
    queryKey: ["dre-report", activeRestaurant?.id, dateFrom, dateTo],
    queryFn: async (): Promise<DREData> => {
      if (!activeRestaurant) throw new Error("No restaurant");

      const startDate = `${dateFrom}T00:00:00`;
      const endDate = `${dateTo}T23:59:59`;

      // 1. Fetch orders for revenue calculation
      const { data: orders, error: ordersError } = await supabase
        .from("orders")
        .select("id, subtotal, discount, additional_fees, delivery_fee, total, status, created_at")
        .eq("restaurant_id", activeRestaurant.id)
        .gte("created_at", startDate)
        .lte("created_at", endDate);

      if (ordersError) throw ordersError;

      // 2. Fetch expenses for the period
      const { data: expenses, error: expensesError } = await supabase
        .from("expenses" as any)
        .select(`
          id,
          amount,
          status,
          category_id,
          expense_date
        `)
        .eq("restaurant_id", activeRestaurant.id)
        .gte("expense_date", dateFrom)
        .lte("expense_date", dateTo)
        .in("status", ["open", "paid"]);

      if (expensesError) throw expensesError;

      // 3. Fetch expense categories with chart account linkage
      const { data: expenseCategories, error: catError } = await supabase
        .from("expense_categories" as any)
        .select("id, name, chart_account_id")
        .eq("restaurant_id", activeRestaurant.id);

      if (catError) throw catError;

      // 4. Fetch chart of accounts
      const { data: chartAccounts, error: chartError } = await supabase
        .from("chart_of_accounts" as any)
        .select("id, code, name, account_type, parent_id")
        .eq("restaurant_id", activeRestaurant.id);

      if (chartError) throw chartError;

      // 5. Fetch stock movements for CMV calculation (type = 'sale')
      const { data: stockMovements, error: stockError } = await supabase
        .from("stock_movements")
        .select("id, quantity, unit_cost, type, created_at")
        .eq("restaurant_id", activeRestaurant.id)
        .eq("type", "sale")
        .gte("created_at", startDate)
        .lte("created_at", endDate);

      if (stockError) throw stockError;

      // 6. Fetch payment methods for card fee calculation and revenue classification
      const { data: paymentMethods, error: pmError } = await supabase
        .from("payment_methods")
        .select("id, name, fee_percentage, chart_account_id")
        .eq("restaurant_id", activeRestaurant.id);

      if (pmError) throw pmError;

      // 7. Fetch order payments for fee calculation (delivered = completed)
      const deliveredOrderIds = (orders || []).filter(o => o.status === "delivered").map(o => o.id);
      let orderPayments: any[] = [];
      if (deliveredOrderIds.length > 0) {
        const { data: payments, error: payError } = await supabase
          .from("order_payments")
          .select("order_id, payment_method_id, amount")
          .in("order_id", deliveredOrderIds);

        if (!payError && payments) {
          orderPayments = payments;
        }
      }

      // Calculate revenue from delivered orders (delivered = completed in this system)
      const completedOrders = (orders || []).filter((o) => o.status === "delivered");
      const cancelledOrders = (orders || []).filter((o) => o.status === "cancelled");

      const productSales = completedOrders.reduce((sum, o) => sum + Number(o.subtotal || 0), 0);
      const serviceTax = completedOrders.reduce((sum, o) => sum + Number(o.additional_fees || 0), 0);
      const deliveryFees = completedOrders.reduce((sum, o) => sum + Number(o.delivery_fee || 0), 0);

      const grossRevenue = {
        productSales,
        serviceTax,
        deliveryFees,
        total: productSales + serviceTax + deliveryFees,
      };

      // Calculate deductions
      const discounts = completedOrders.reduce((sum, o) => sum + Number(o.discount || 0), 0);
      const cancellations = cancelledOrders.reduce((sum, o) => sum + Number(o.total || 0), 0);

      const deductions = {
        discounts,
        cancellations,
        total: discounts + cancellations,
      };

      const netRevenue = grossRevenue.total - deductions.total;

      // Calculate CMV from stock movements (type = 'sale' means cost deduction)
      const cmvTotal = ((stockMovements as any[]) || []).reduce(
        (sum, m) => sum + Math.abs(Number(m.quantity || 0)) * Number(m.unit_cost || 0),
        0
      );

      const cmv = { total: cmvTotal };

      const grossProfit = netRevenue - cmv.total;
      const grossMarginPercent = netRevenue > 0 ? (grossProfit / netRevenue) * 100 : 0;

      // Calculate operating expenses by account
      const categoryMap = new Map((expenseCategories as any[] || []).map((c: any) => [c.id, c]));
      const accountMap = new Map((chartAccounts as any[] || []).map((a: any) => [a.id, a]));

      const expenseByAccount = new Map<string, number>();
      const expensesByCategory = new Map<string, number>();

      (expenses as any[] || []).forEach((exp: any) => {
        const category = categoryMap.get(exp.category_id);
        const amount = Number(exp.amount || 0);

        // Group by category first
        const catId = exp.category_id || "unknown";
        expensesByCategory.set(catId, (expensesByCategory.get(catId) || 0) + amount);

        // If category is linked to a chart account, use it
        if (category?.chart_account_id) {
          const accountId = category.chart_account_id;
          expenseByAccount.set(accountId, (expenseByAccount.get(accountId) || 0) + amount);
        } else {
          // Use "other" category
          expenseByAccount.set("other", (expenseByAccount.get("other") || 0) + amount);
        }
      });

      const operatingExpenseItems: DREAccountItem[] = [];
      let operatingTotal = 0;

      expenseByAccount.forEach((amount, accountId) => {
        if (accountId === "other") {
          operatingExpenseItems.push({
            accountId: "other",
            accountCode: "4.9",
            accountName: "Outras Despesas",
            amount,
          });
        } else {
          const account = accountMap.get(accountId);
          if (account && account.account_type === "fixed_expense") {
            operatingExpenseItems.push({
              accountId,
              accountCode: account.code,
              accountName: account.name,
              amount,
            });
          }
        }
        operatingTotal += amount;
      });

      // Sort by code
      operatingExpenseItems.sort((a, b) => a.accountCode.localeCompare(b.accountCode));

      const operatingExpenses = {
        byAccount: operatingExpenseItems,
        total: operatingTotal,
      };

      const operatingResult = grossProfit - operatingExpenses.total;
      const operatingMarginPercent = netRevenue > 0 ? (operatingResult / netRevenue) * 100 : 0;

      // Calculate card fees from payments
      const pmMap = new Map((paymentMethods || []).map((pm) => [pm.id, pm]));
      let cardFeesTotal = 0;

      orderPayments.forEach((payment) => {
        const pm = pmMap.get(payment.payment_method_id);
        if (pm && pm.fee_percentage > 0) {
          cardFeesTotal += Number(payment.amount || 0) * (pm.fee_percentage / 100);
        }
      });

      // Get other financial expenses from expenses linked to financial accounts
      let otherFinancialFees = 0;
      expenseByAccount.forEach((amount, accountId) => {
        const account = accountMap.get(accountId);
        if (account && account.account_type === "financial_expense") {
          otherFinancialFees += amount;
          // Remove from operating expenses if already counted
          const idx = operatingExpenseItems.findIndex((i) => i.accountId === accountId);
          if (idx >= 0) {
            operatingExpenses.total -= operatingExpenseItems[idx].amount;
            operatingExpenseItems.splice(idx, 1);
          }
        }
      });

      const financialExpenses = {
        cardFees: cardFeesTotal,
        otherFees: otherFinancialFees,
        total: cardFeesTotal + otherFinancialFees,
      };

      const netProfit = operatingResult - financialExpenses.total;
      const netMarginPercent = netRevenue > 0 ? (netProfit / netRevenue) * 100 : 0;

      return {
        period: { from: dateFrom, to: dateTo },
        grossRevenue,
        deductions,
        netRevenue,
        cmv,
        grossProfit,
        grossMarginPercent,
        operatingExpenses,
        operatingResult,
        operatingMarginPercent,
        financialExpenses,
        netProfit,
        netMarginPercent,
      };
    },
    enabled: !!activeRestaurant && !!dateFrom && !!dateTo,
  });
}
